/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import { Title, Group, Box, Alert } from '@mantine/core';
import { useForm } from '@mantine/form';

// Components
import { Button } from '@components/atoms/Button';
import { Text } from '@components/atoms/Text';
import { TextInput } from '@components/atoms/TextInput';
import { Link } from '@components/atoms/Link';

// Styles
import classes from './ResetEmailForm.module.css';

// Api
import { useResetEmail } from '@features/update-email/api/reset-email';

// I18n
import { useTranslation } from 'react-i18next';

// Router
import { useLocation, useNavigate } from 'react-router';

// Icons
import { IconInfoCircle } from '@tabler/icons-react';
import { ResetEmailData } from '@features/update-email/types';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */
type ResetPasswordProps = {
	id: string;
	currentEmail: string;
	newEmail: string;
	email: string;
	token: string;
	isValidToken: boolean;
	isTokenExpired: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const ResetEmailForm = (
	props: ResetPasswordProps
) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const icon = <IconInfoCircle />;

	const form = useForm({
		initialValues: {
			email: props.email,
			currentEmail: props.currentEmail,
			newEmail: props.newEmail,
		},
		validate: {
			currentEmail: (value) =>
				value.length < 8
					? 'form.error.emailMustHaveEightLetters'
					: null,
			newEmail: (value, values) =>
				value === values.currentEmail
					? 'form.error.emailsDidNotMatch'
					: null,
		},
	});

	const resetEmail = useResetEmail({
		mutationConfig: {
			onSuccess() {
				form.reset();
			},
		},
	});

	/* ******************************** RENDERING ******************************* */
	return (
		<form
			onSubmit={form.onSubmit(() => {
				const payload: ResetEmailData = {
					token: props.token ? props.token : '',
				};
				resetEmail.mutate(payload);
			})}
		>
			<Box className={classes.containerForm}>
				{location.pathname == '/auth/sign_in' ? (
					<Box>
						<Title hiddenFrom="xxl" size="h4">
							{t('resetEmail.chooseEmail')}
						</Title>
						<Title visibleFrom="xxl" size="h3">
							{t('resetEmail.chooseEmail')}
						</Title>
					</Box>
				) : (
					<Box>
						<Title hiddenFrom="xxl" size="h4">
							{t('resetEmail.title')}
						</Title>
						<Title visibleFrom="xxl" size="h3">
							{t('resetEmail.title')}
						</Title>
					</Box>
				)}

				<Text
					hiddenFrom="xxl"
					type="body2"
					color="inputs-texts-icons"
				>
					{t('resetEmail.description')}
				</Text>
				<Text
					visibleFrom="xxl"
					type="body1"
					color="inputs-texts-icons"
				>
					{t('resetEmail.description')}
				</Text>
				<Box className={classes.spacerVertical32} />

				{props.isValidToken ? (
					!props.isTokenExpired ? (
						<>
							<TextInput
								disabled
								size="lg"
								hiddenFrom="xxl"
								radius="8.29px"
								placeholder={t('auth.yourEmailAddress')}
								{...form.getInputProps('currentEmail')}
							/>
							<TextInput
								disabled
								size="xl"
								visibleFrom="xxl"
								radius="8.29px"
								placeholder={t('auth.yourEmailAddress')}
								{...form.getInputProps('currentEmail')}
							/>
							<Box className={classes.spacerVertical24} />
							<TextInput
								disabled
								size="lg"
								hiddenFrom="xxl"
								radius="8.29px"
								placeholder={t('auth.yourEmailAddress')}
								{...form.getInputProps('newEmail')}
							/>
							<TextInput
								disabled
								size="xl"
								visibleFrom="xxl"
								radius="8.29px"
								placeholder={t('auth.yourEmailAddress')}
								{...form.getInputProps('newEmail')}
							/>
							<Box
								className={
									classes.containerForgetPassword
								}
							></Box>
							<Button
								visibleFrom="xxl"
								size="lg"
								fullWidth
								type="submit"
								loading={
									resetEmail && resetEmail.isLoading
								}
							>
								{t('global.submit')}
							</Button>
							<Button
								hiddenFrom="xxl"
								size="md"
								fullWidth
								type="submit"
								loading={
									resetEmail && resetEmail.isLoading
								}
							>
								{t('global.submit')}
							</Button>
							<Group justify="center" mt={10}>
								<Link
									onClick={() => {
										navigate('/login');
									}}
									color="blue"
									size="h6"
								>
									{t('global.return')}
								</Link>
							</Group>
						</>
					) : (
						<Alert
							variant="light"
							color="error"
							title={t(
								'resetEmail.emailResetExpiredLinkIssue'
							)}
							icon={icon}
						>
							{t(
								'resetEmail.emailResetExpiredLinkIssueDescription'
							)}
						</Alert>
					)
				) : (
					<Alert
						variant="light"
						color="error"
						title={t('resetEmail.emailResetLinkIssue')}
						icon={icon}
					>
						{t('resetEmail.emailResetLinkIncorrect')}
					</Alert>
				)}
			</Box>
		</form>
	);
};
